import auth0 from '@/auth/auth0Plugin';
import { captureError } from '@smg-real-estate/browser-rum';

export interface MessageCount {
  count: number;
}

export interface ListingUnreadMessageCount extends MessageCount {
  smg_listing: string;
  created: string | null;
}

export interface RoleInternalUnreadMessageCount extends MessageCount {
  role: string;
  internal: boolean;
}

export class FlatfoxApiError extends Error {
  endpoint: string;

  constructor(endpoint: string | URL, message: string) {
    super(`Flatfox API error: ${message}`);
    this.name = 'FlatfoxApiError';
    this.endpoint = endpoint instanceof URL ? endpoint.toString() : endpoint;
  }
}

async function fetchFromFlatfox<T>(url: URL, customerId: string, authToken: string): Promise<T> {
  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Flatfox-Org-Switcher-Smg-Customer': customerId,
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    captureError(new FlatfoxApiError(url, errorMessage));
    throw new FlatfoxApiError(url, errorMessage);
  }
}

function buildUnreadStateUrl(groupBy: string[], query: Record<string, string>) {
  const endpointUrl = new URL(
    `api/v1/unread-state/groupby/${groupBy.join('/')}/`,
    import.meta.env.VUE_FLATFOX_URL,
  );

  for (const [key, value] of Object.entries(query)) {
    endpointUrl.searchParams.set(key, value);
  }

  return endpointUrl;
}

function getFlatfoxAuthToken(): Promise<string> {
  return auth0.getAccessTokenSilently({
    authorizationParams: {
      audience: import.meta.env.VUE_FLATFOX_URL,
    },
  });
}

export async function getListingUnreadMessageCounts(
  listingIds: string[],
  customerId: string,
): Promise<ListingUnreadMessageCount[]> {
  if (!listingIds.length) {
    return [];
  }

  if (!customerId) {
    throw new FlatfoxApiError('', 'No active agency ID available');
  }

  // Request token with Flatfox audience to ensure proper API authorization
  const authToken = await getFlatfoxAuthToken();

  const url = buildUnreadStateUrl(['smg_listing'], {
    smg_listing_ids: listingIds.join(','),
    participant: 'own',
    role: 'advertiser',
  });

  return fetchFromFlatfox(url, customerId, authToken);
}

export async function getTotalUnreadMessageCounts(
  customerId: string,
): Promise<RoleInternalUnreadMessageCount[]> {
  if (!customerId) {
    throw new FlatfoxApiError('', 'No active agency ID available');
  }

  // Request token with Flatfox audience to ensure proper API authorization
  const authToken = await getFlatfoxAuthToken();

  const url = buildUnreadStateUrl(['role', 'internal'], {
    participant: 'own',
    role: 'advertiser',
  });

  return fetchFromFlatfox(url, customerId, authToken);
}
